<template>
    <div class="px-sm-6">
        <breadcrumbs :text="data.name" class="pb-3" />
        <h1 v-if="title" v-text="title" />
        <div class="text-body-2 mt-4 cms-wrapper">
            <div v-html="html" />
        </div>
    </div>
</template>

<script setup lang="ts">
    import Breadcrumbs from '~/components/Breadcrumbs.vue'
    import {CHANGE_LOCALE_EVENT, EventBus} from '@/utils/event-bus'
    import {fullUrl} from '~src/utils/host-adapter'
    import MarkdownIt from 'markdown-it/dist/markdown-it.js'

    const md = new MarkdownIt('default', {html: true})

    const app = useNuxtApp()
    const route = useRoute()
    const link = route.params.link as string
    const {data, error, refresh} = await useAsyncData(`info-${link}`, async () => {
        const {name, title, content, head: seo} = await app.$api.infoPage.get(link)
        return {name, title, content, seo}
    })

    if (error.value) {
        if (link.startsWith('terms-of-use-') && error.value.statusCode === 404) {
            await navigateTo('/info/terms-of-use')
        } else if (link.startsWith('privacy-policy-') && error.value.statusCode === 404) {
            await navigateTo('/info/privacy-policy')
        } else {
            throw createError({statusCode: error.value.statusCode, message: error.value.message})
        }
    } else {
        useHead({
            link: [
                {
                    rel: 'canonical',
                    href: fullUrl(route.path),
                },
            ],
            title: computed(() => (data.value.seo.title ? `- ${data.value.seo.title}` : null)),
            meta: computed(() =>
                data.value.seo.meta.reduce((result: any[], meta: any) => {
                    const metaIndex = result.findIndex(({hid}) => hid === meta.hid)
                    if (metaIndex !== -1) {
                        result[metaIndex] = meta
                    } else {
                        result.push(meta)
                    }
                    return result
                }, [])
            ),
        })
    }

    const html = computed(() => md.render(data.value.content))
    const title = computed(() => data.value.title.trim())

    onMounted(() => {
        EventBus.$on(CHANGE_LOCALE_EVENT, refresh)
    })

    onBeforeUnmount(() => {
        EventBus.$off(CHANGE_LOCALE_EVENT, refresh)
    })
</script>
